import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { provideNativeDateAdapter } from '@angular/material/core';
import { PatientFilterComponent } from '../shared/patient-filter/patient-filter.component';
import { FhirResourceService } from '../shared/fhir-resource.service';
import { backendEndPointToken, appointmentTypesToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { HttpClient, HttpContext } from '@angular/common/http';
import { LoadingUIEnabled } from '../loading.interceptor';
import { UtilityService } from '../shared/utility.service';
import { map } from 'rxjs/operators';
import { Practitioner, Slot, Appointment, PractitionerRole, Bundle } from 'fhir/r4';
import { ErrorService } from '../shared/error.service';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../shared/auth/auth.service';
import { Observable, EMPTY } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';

export interface PractitionerOption {
  id: string;
  name: string;
  roleDisplay: string;
}

@Component({
  selector: 'app-make-appointments',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatCardModule, MatButtonModule,
    MatSelectModule, MatAutocompleteModule, MatDatepickerModule, MatIconModule, MatInputModule, MatChipsModule,
    PatientFilterComponent
  ],
  templateUrl: './make-appointments.component.html',
  styleUrl: './make-appointments.component.scss',
  providers: [provideNativeDateAdapter()]
})
export class MakeAppointmentsComponent implements OnInit {
  fb = inject(FormBuilder);
  fhirResourceService = inject(FhirResourceService);
  backendUrl = inject(backendEndPointToken);
  appointmentTypes = inject(appointmentTypesToken);
  errorService = inject(ErrorService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  http = inject(HttpClient);
  utilityService = inject(UtilityService);
  authService = inject(AuthService);
  terminologyUrl = inject(terminologyCanonicalUrlBaseToken);
  
  bookingForm: FormGroup;
  practitionerSearchCtrl = new FormControl();
  practitioners: PractitionerOption[] = [];
  filteredPractitioners: PractitionerOption[] = [];
  slots: Slot[] = [];
  loadingSlots = false;
  selectedPatientId: string | null = null;
  loadingPractitioners = false;
  
  // Filtered appointment types for the receptionist
  allowedAppointmentTypes: any[] = [];

  constructor() {
    this.bookingForm = this.fb.group({
      practitioner: ['', Validators.required],
      appointmentType: ['', Validators.required],
      date: [new Date(), Validators.required],
      slot: ['', Validators.required]
    });
  }

  ngOnInit() {
    this.loadPractitioners();

    this.practitionerSearchCtrl.valueChanges.subscribe(val => {
      const search = (typeof val === 'string' ? val : val?.name)?.toLowerCase() || '';
      this.filteredPractitioners = this.practitioners.filter(p => 
        p.name.toLowerCase().includes(search) || p.roleDisplay.toLowerCase().includes(search)
      );
    });
    
    // Restrict appointment types to typical outpatient flows for the Receptionist
    const allowedCodes = ['patient-consultation', 'follow-up', 'routine-checkup']; 
    this.allowedAppointmentTypes = this.appointmentTypes.filter((t: any) => allowedCodes.includes(t.code));

    // If the original tokens didn't have these specific codes, fallback to whatever is available 
    // but exclude explicitly internal things like 'ward-rounds' if possible.
    if (this.allowedAppointmentTypes.length === 0) {
       this.allowedAppointmentTypes = this.appointmentTypes.filter((t: any) => !t.code.includes('ward') && !t.code.includes('surgery'));
    }

    this.route.queryParams.subscribe(params => {
      this.selectedPatientId = params['patientId'] || null;
    });

    // Trigger slot search when practitioner, appointmentType, or date changes
    this.bookingForm.get('practitioner')?.valueChanges.subscribe(() => this.searchSlots());
    this.bookingForm.get('appointmentType')?.valueChanges.subscribe(() => this.searchSlots());
    this.bookingForm.get('date')?.valueChanges.subscribe(() => this.searchSlots());
  }
  
  displayPractitionerFn(p?: PractitionerOption): string {
    return p ? `${p.name} (${p.roleDisplay})` : '';
  }

  onPractitionerSelected(event: any) {
    this.bookingForm.patchValue({ practitioner: event.option.value.id });
  }

  onPatientSelected(patientId: string) {
    this.selectedPatientId = patientId;
  }

  private loadPractitioners() {
    this.loadingPractitioners = true;
    const ctx = new HttpContext().set(LoadingUIEnabled, false);
    
    // Explicitly scope by organization to ensure server-side filtering handles pagination correctly.
    // The loading interceptor would catch unscoped requests via client-side filtering, 
    // but that could result in empty pages if the first 200 roles belong to other hospitals.
    const user: any = this.authService.user.getValue();
    const orgId = user?.organizationId;
    
    let url = `${this.backendUrl}/PractitionerRole?_include=PractitionerRole:practitioner&_count=200`;
    if (orgId) {
      url += `&organization=Organization/${orgId}`;
    }

    // Use raw HTTP and RxJS expand to handle pagination manually.
    // We cannot use utilityService.fetchAll here because it actively strips out
    // the included Practitioner resources (it strictly filters by resourceType).
    this.http.get<Bundle<any>>(url, { context: ctx }).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= 100) return EMPTY; // Max 100 pages
        const nextLink = (bundle?.link ?? []).find(
          (link) => String(link?.relation ?? '').toLowerCase() === 'next'
        )?.url;
        const nextUrl = nextLink ? nextLink.replace(/^http:\/\/[^\/]+/, this.backendUrl).replace(/^https:\/\/[^\/]+/, this.backendUrl) : null;
        return nextUrl ? this.http.get<Bundle<any>>(nextUrl, { context: ctx }) : EMPTY;
      }),
      map(bundle => (bundle?.entry ?? []).map(entry => entry.resource).filter(Boolean)),
      reduce((acc, curr) => acc.concat(curr), [] as any[])
    )
    .subscribe({
        next: (resources) => {
          const roles = resources.filter((r: any) => r.resourceType === 'PractitionerRole') as PractitionerRole[];
          const practs = resources.filter((r: any) => r.resourceType === 'Practitioner') as Practitioner[];
          
          const options: PractitionerOption[] = [];
          
          const ROLE_MAP: Record<string, string> = {
            'alliedprofessionalhealth': 'Allied Professional Health',
            'generalpractitioner': 'General Practitioner',
            'specialist': 'Specialist',
            'nursemidwife': 'Nurse / Midwife',
            'surgeon': 'Surgeon'
          };
          
          roles.forEach(role => {
            const pRef = role.practitioner?.reference;
            if (pRef) {
              const pId = pRef.replace('Practitioner/', '');
              const p = practs.find(pract => pract.id === pId);
              if (p) {
                const name = this.getPractitionerDisplay(p);
                let rawRole = role.code?.[0]?.coding?.[0]?.display || role.code?.[0]?.text || 'General Practitioner';
                
                const lowerRole = rawRole.toLowerCase().replace(/[^a-z]/g, '');
                const roleDisplay = ROLE_MAP[lowerRole] || rawRole;

                options.push({
                  id: p.id!,
                  name: name,
                  roleDisplay: roleDisplay
                });
              }
            }
          });
          
          // Remove duplicates if a practitioner has multiple roles
          this.practitioners = options.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i);
          this.filteredPractitioners = [...this.practitioners];
          this.loadingPractitioners = false;
        },
        error: () => this.loadingPractitioners = false
    });
  }

  getPractitionerDisplay(practitioner: Practitioner): string {
    const name = practitioner.name?.[0];
    const prefix = Array.isArray(name?.prefix) ? name?.prefix?.join(' ') : '';
    const given = Array.isArray(name?.given) ? name?.given?.join(' ') : '';
    const family = name?.family ?? '';
    return `${prefix} ${given} ${family}`.trim() || 'Unnamed Practitioner';
  }

  searchSlots() {
    const practitionerId = this.bookingForm.value.practitioner;
    const typeCode = this.bookingForm.value.appointmentType;
    const dateObj = this.bookingForm.value.date;

    if (!practitionerId || !typeCode || !dateObj) {
      this.slots = [];
      return;
    }

    this.loadingSlots = true;
    
    // Format date as YYYY-MM-DD
    const isoDate = typeof dateObj.toISOString === 'function' ? dateObj.toISOString() : new Date(dateObj).toISOString();
    const dateStr = isoDate.split('T')[0];
    
    // Fetch free slots on the given date, resolving the schedule reference
    const queries = `status=free&start=ge${dateStr}T00:00:00Z&start=le${dateStr}T23:59:59Z&_include=Slot:schedule`;

    const ctx = new HttpContext().set(LoadingUIEnabled, false);
    
    // Bypass utilityService.fetchAll because it strips out included resources (like Schedule)
    const url = `${this.backendUrl}/Slot?${queries}`;
    this.http.get<Bundle<any>>(url, { context: ctx }).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= 50) return EMPTY; // Max 50 pages
        const nextLink = (bundle?.link ?? []).find(
          (link) => String(link?.relation ?? '').toLowerCase() === 'next'
        )?.url;
        const nextUrl = nextLink ? nextLink.replace(/^http:\/\/[^\/]+/, this.backendUrl).replace(/^https:\/\/[^\/]+/, this.backendUrl) : null;
        return nextUrl ? this.http.get<Bundle<any>>(nextUrl, { context: ctx }) : EMPTY;
      }),
      map(bundle => (bundle?.entry ?? []).map(entry => entry.resource).filter(Boolean)),
      reduce((acc, curr) => acc.concat(curr), [] as any[])
    ).subscribe({
      next: (resources) => {
        const slots = resources.filter((r: any) => r.resourceType === 'Slot') as Slot[];
        const schedules = resources.filter((r: any) => r.resourceType === 'Schedule');

        const scheduleMap = new Map<string, any>();
        schedules.forEach((s: any) => scheduleMap.set(`Schedule/${s.id}`, s));

        this.slots = slots.filter(slot => {
          const scheduleRef = slot.schedule.reference;
          const schedule = scheduleMap.get(scheduleRef || '');
          if (!schedule) return false;

          // Check Practitioner
          const isActor = schedule.actor?.some((a: any) => a.reference === `Practitioner/${practitionerId}`);
          if (!isActor) return false;

          // Check Task Type (serviceCategory can be on slot or schedule)
          const slotCategoryCode = slot.serviceCategory?.[0]?.coding?.[0]?.code;
          const scheduleCategoryCode = schedule.serviceCategory?.[0]?.coding?.[0]?.code;
          const hasCorrectType = slotCategoryCode === typeCode || scheduleCategoryCode === typeCode;
          if (!hasCorrectType) return false;

          return true;
        });
        
        this.bookingForm.patchValue({ slot: '' });
        this.loadingSlots = false;
      },
      error: () => this.loadingSlots = false
    });
  }

  formatSlotTime(slot: Slot): string {
    if (!slot.start || !slot.end) return 'Unknown Time';
    return `${new Date(slot.start).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${new Date(slot.end).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
  }

  submitAppointment() {
    if (this.bookingForm.invalid) {
      this.errorService.openandCloseError('Please complete all form fields.');
      return;
    }

    if (!this.selectedPatientId) {
      this.errorService.openandCloseError('Please select a patient.');
      return;
    }

    const { slot: slotId, appointmentType } = this.bookingForm.value;

    const slotObj = this.slots.find(s => s.id === slotId);
    let minutesDuration: number | undefined;
    if (slotObj && slotObj.start && slotObj.end) {
      minutesDuration = (new Date(slotObj.end).getTime() - new Date(slotObj.start).getTime()) / 60000;
    }

    const appointment: Appointment = {
      resourceType: 'Appointment',
      status: 'booked',
      start: slotObj?.start,
      end: slotObj?.end,
      minutesDuration: minutesDuration,
      serviceCategory: [{
        coding: [{
          system: `${this.terminologyUrl}/CodeSystem/appointment-service-category`,
          code: 'consultation',
          display: 'Consultation'
        }]
      }],
      appointmentType: {
        coding: [{
          system: 'http://e-likita.org/fhir/CodeSystem/appointment-mode',
          code: 'in-person',
          display: 'In-Person'
        }]
      },
      specialty: [{
        coding: [{
          system: `${this.terminologyUrl}/CodeSystem/specialties`,
          code: appointmentType,
          display: this.appointmentTypes.find((t: any) => t.code === appointmentType)?.display
        }]
      }],
      participant: [
        {
          actor: { reference: `Patient/${this.selectedPatientId}` },
          status: 'accepted'
        },
        {
          actor: { reference: `Practitioner/${this.bookingForm.value.practitioner}` },
          status: 'accepted'
        }
      ],
      slot: [{ reference: `Slot/${slotId}` }]
    };

    this.fhirResourceService.postResource('Appointment', appointment).subscribe({
      next: () => {
        this.router.navigate(['/app/appointments']);
      },
      error: () => this.errorService.openandCloseError('Failed to book appointment.')
    });
  }
}
